// @flow

/**
 * Faceted search in the context of elastic basically just refers to tag-based
 * search of products. The `Facet` type carries information about each
 * searchable term (each tag value) and how many relevant results there are in
 * each searchable term given the current search context.
 *
 * Basically the point of this is to be able to show the numbers in the
 * following example:
 *
 * - [ ] Category (0)
 *   - [ ] Active (0)
 *   - [*] Casual (25)
 *   - [ ] Running (0)
 * - [ ] Gender (25)
 *   - [ ] Male (15)
 *   - [ ] Female (10)
 *   - [ ] Unisex (0)
 *
 * The main value here is in the user knowing how many results they would get
 * were they to select one of these other tag values without actually having to
 * go through the process of selecting it. Hunting through tags all with zero
 * results can be a real pain.
 */

/**
 * A tag value and its corresponding number of results.
 */
export type FacetTerm = [string, number];

export type SerializedFacet = $ReadOnly<{|
  // number of items that have no value for the field
  missing: number,
  // number of items that are theoretically relevant to this facet. note that
  // this DOES include anything that might be categorized as "other."
  total: number,
  // number of items that are theoretically relevant to this facet but could not
  // be included because of the static limit on the number of terms returned in
  // the facet. basically, if this number is greater than zero then it means we
  // have incomplete information about this facet. this is basically "total"
  // minus the sum of all the term values.
  other: number,
  terms: $ReadOnlyArray<FacetTerm>,
|}>;

/**
 * The parent tag with a name and containing a list of tag values.
 */
export type Facet = $ReadOnly<{|
  ...SerializedFacet,
  name: string,
|}>;

// keep in mind that this is a flat structure. i believe nested tags get
// flattened out into this structure, dumbly. not sure yet about the nuances of
// how this happens.
export type SerializedFacetMap = $ReadOnly<{ [name: string]: SerializedFacet }>;

type AmbiguousTotal = [number];

export type FacetMathFigure = number | AmbiguousTotal | null | void;

export const NESTING_SEPARATOR = '~';

export const deserializedFacetList = (
  facetMap: SerializedFacetMap,
): $ReadOnlyArray<Facet> =>
  (Object.entries(facetMap): any[]).map(
    ([name, serializedFacet]: [string, SerializedFacet]) => ({
      ...serializedFacet,
      name,
    }),
  );

export const isComprehensive = (facet: Facet): boolean => facet.other === 0;

export const byName = (
  haystack: $ReadOnlyArray<Facet>,
  needle: string,
): Facet | null => haystack.find((f) => f.name === needle) || null;

// `null` return value here means the term count is unknown
export const termCountByPath = (
  facet: Facet,
  path: $ReadOnlyArray<string>,
): number | null =>
  (facet.terms.find(([t]) => t === path.slice(1).join(NESTING_SEPARATOR)) ||
    [])[1] || (isComprehensive(facet) ? 0 : null);

export const termName = (path: $ReadOnlyArray<string>): string =>
  path.slice(1).join('~');
